import BaseAccordion from 'components/Accordions/BaseAccordion';
import SearchBar from 'components/SearchBar/SearchBar';
import BaseTable from 'components/Tables/BaseTable';
import { usePerms } from 'contexts/PermsContext';
import { useMemo, useState } from 'react';
import { Button, Container, Nav } from 'react-bootstrap';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import ActionButtons from './components/ActionButtons.component';
import Deployments from './components/Deployments';
import StatusDot from './components/StatusDot';
import { STATUS_VIEW } from './config/ServiceManager.config';
import styles from './css/ServiceManager.module.css';
import { useServices } from './hooks/ServiceManager.hooks';
import AddServiceModal from './modals/AddServiceModal.component';
import CommandHistoryModal from './modals/CommandHistoryModal.component';
import ServiceActionModal from './modals/ServiceActionModal.component';
import ServiceManagerHelpModal from './modals/ServiceManagerHelpModal';
import type { ServiceRecord } from './types/ServiceManager.types';
import { canBeModified, canRestart, isRestartOnlyService } from './utils/ServiceManager.utils';
import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';

type TabKey = 'services' | 'deployments';

/**
 * Why a service offers no controls. Names which of the three reasons applies
 * rather than blaming "system service" for all of them: a locked service, one
 * the supervisor only watches, and a protected core service are different
 * situations and an operator should be able to tell them apart.
 */
function noActionsReason(service: ServiceRecord): string {
  if (service.locked) return 'Locked by the supervisor';
  if (isRestartOnlyService(service)) return 'Monitored only, not controlled from here';
  return 'Actions disabled for a protected system service';
}

/**
 * Main ServiceManager page component.
 * Displays a table of PM2 services with action controls, search,
 * and modals for adding services, confirming actions, and viewing command history.
 */
export default function ServiceManager() {
  const [params, setParams] = useSearchParams();
  const search = params.get('search') || '';
  const perms = usePerms();
  // Gate the Deployments tab on `deployment_manager` access entirely (per
  // operator preference 2026-05-15 — the spec allows read-only with just
  // `service_manager`, but we want the tab hidden if the operator can't
  // act on anything inside it). Same predicate the tab's mutating controls
  // use, so it's all-or-nothing.
  const showDeployments: boolean = perms?.hasDeploymentManagerAccess?.() ?? false;
  // If the URL says ?tab=deployments but the user doesn't have access, fall
  // back to Services. Prevents a direct link from rendering the empty tab.
  const activeTab: TabKey =
    params.get('tab') === 'deployments' && showDeployments ? 'deployments' : 'services';

  const setActiveTab = (key: TabKey) => {
    const next = new URLSearchParams(params);
    if (key === 'services') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const {
    services,
    filteredServices,
    filteredGlobalServices,
    removedServices,
    isLoading,
    isRefreshing,
    reqTime,
    pendingAction,
    rowIdEllipsisOpen,
    mobileRowIdEllipsisOpen,
    fetchServices,
    handleActionChange,
    handleActionConfirm,
    handleActionCancel,
    handleAddService,
    handleRemoveService,
    setRowIdEllipsisOpen,
    setMobileRowIdEllipsisOpen,
  } = useServices({
    search,
    // Operators who can manage deployments work across all of them, so they
    // get the unscoped listing. Everyone else sees their own workplace's
    // services plus globals, which is what the proxy returns by default from
    // the workplace id on their token.
    allWorkplaces: showDeployments,
  });

  // The supervisor tags every row with a scope, so globals split out for every
  // user rather than only for deployment managers. The section still hides when
  // there is nothing in it, which is the case on a supervisor running none.
  const hasGlobals: boolean = filteredGlobalServices.length > 0;

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        size: 80,
        maxSize: 80,
        Cell: ({ cell }: { cell: { getValue: () => number } }) => (
          <div className="text-theme small">{cell.getValue()}</div>
        ),
      },
      {
        header: 'Service',
        accessorKey: 'name',
        size: 240,
        Cell: ({ cell }: { cell: { getValue: () => string } }) => (
          <div className="text-theme small">{cell.getValue()}</div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        Cell: ({ row }: { row: { original: ServiceRecord } }) => (
          <StatusDot view={STATUS_VIEW[row.original.status] ?? STATUS_VIEW.Unknown} />
        ),
      },
      {
        header: 'Uptime',
        accessorKey: 'uptime',
        Cell: ({ cell }: { cell: { getValue: () => string } }) => (
          <div className="text-theme small">{cell.getValue()}</div>
        ),
      },
      {
        header: 'Unstable Restarts',
        accessorKey: 'unstableRestart',
        Cell: ({ cell }: { cell: { getValue: () => number } }) => (
          <div className="text-theme small">{cell.getValue()}</div>
        ),
      },
      {
        header: 'Manual Restarts',
        accessorKey: 'manualRestarts',
        Cell: ({ cell }: { cell: { getValue: () => number } }) => (
          <div className="text-theme small">{cell.getValue()}</div>
        ),
      },
      {
        header: '',
        id: 'Actions',
        columnDefType: 'display',
        enableSorting: false,
        size: 220,
        Cell: ({ row }: { row: { original: ServiceRecord } }) => {
          const service = row.original;
          // Restart stays available for services the supervisor only watches:
          // it acts on a process that is already running, so it can neither
          // resurrect a deliberately stopped service nor start a duplicate.
          if (!canRestart(service)) {
            return <div className="text-theme-muted small">{noActionsReason(service)}</div>;
          }
          return (
            <ActionButtons
              status={service.status}
              restartOnly={!canBeModified(service)}
              onChange={(action) => handleActionChange(service, action)}
            />
          );
        },
      },
      {
        header: '',
        id: 'Menu',
        columnDefType: 'display',
        enableSorting: false,
        size: 80,
        Cell: ({ row }: { row: { original: ServiceRecord } }) => {
          const service = row.original;
          const id = service.id;

          if (!canBeModified(service)) {
            return <div className="text-theme-muted small"></div>;
          }

          return (
            <BaseEllipsisMenu
              options={[
                {
                  component: (
                    <div className="w-100 d-flex flex-column">
                      <Button
                        className="w-100 px-5 text-center cursor-pointer text-theme border border-1 border-black"
                        variant="danger"
                        onClick={() => handleRemoveService(service)}
                      >
                        Remove
                      </Button>
                    </div>
                  ),
                },
              ]}
              open={rowIdEllipsisOpen[id]}
              onOpen={() =>
                setRowIdEllipsisOpen({
                  ...rowIdEllipsisOpen,
                  [id]: true,
                })
              }
              onClose={() =>
                setRowIdEllipsisOpen({
                  ...rowIdEllipsisOpen,
                  [id]: false,
                })
              }
            />
          );
        },
      },
    ],
    [rowIdEllipsisOpen, handleActionChange, handleRemoveService, setRowIdEllipsisOpen],
  );

  return (
    <Container fluid className="py-3 px-0 d-flex flex-column">
      <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={(key) => setActiveTab((key as TabKey) ?? 'services')}
        className={`mb-2 px-2 ${styles.tabs}`}
      >
        {/* Neither tab carries its own colours. Bootstrap ships no dark
            theming for .nav-tabs, and the previous inline background on this
            one alone made the two look like different controls; the module
            styles both from the same rules. */}
        <Nav.Item>
          <Nav.Link eventKey="services">Services</Nav.Link>
        </Nav.Item>
        {showDeployments && (
          <Nav.Item>
            <Nav.Link eventKey="deployments">Deployments</Nav.Link>
          </Nav.Item>
        )}
        <Button
          variant="link"
          className="ms-auto text-theme-muted text-decoration-none d-flex align-items-center gap-1 px-2"
          onClick={() => setShowHelpModal(true)}
          aria-label="Service Manager help"
        >
          <FaRegQuestionCircle aria-hidden="true" /> Help
        </Button>
      </Nav>

      {activeTab === 'deployments' ? (
        <div className="d-flex flex-column">
          <Deployments />
        </div>
      ) : (
        <div className="bg-base p-3 d-flex flex-column rounded-3">
          <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 mb-3">
            <div className="p-0">
              <div className="text-theme fw-semibold fs-5">Services</div>
              <div className="text-theme small mt-1">
                Showing your workplace&rsquo;s services{hasGlobals ? ' and globals' : ''}
                {reqTime && (
                  <>
                    {' · '}Last requested:{' '}
                    {reqTime.toLocaleString(undefined, { timeZoneName: 'short' })}
                  </>
                )}
              </div>
            </div>
            <div className="p-0 d-flex justify-content-end align-items-center gap-2 flex-grow-1">
              <Button
                disabled={isLoading || isRefreshing}
                onClick={() => fetchServices({ showSpinner: false })}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <SearchBar
                className={`max-w-xs rounded-3 ${styles.searchField}`}
                inputClassName=""
                disabled={false}
              />
            </div>
          </div>

          {/* Match Deployments' "Workplaces" section-header pattern so the two
              tabs read the same. The label is only useful when we're also
              going to render a "Global services" section below — otherwise
              the page-level "Services" title is enough on its own. */}
          {hasGlobals && <div className="text-theme fw-semibold mb-2">Workplace services</div>}

          {isLoading && (
            <div className="text-theme-muted py-4 text-center">Loading services...</div>
          )}

          {!isLoading && filteredServices.length === 0 ? (
            <div className="text-theme-muted py-3">No services to display</div>
          ) : (
            <>
              <div className="d-none d-lg-block">
                <BaseTable
                  columns={columns}
                  rows={filteredServices}
                  expandable
                  onRowClick={() => {}}
                  customStyles={{
                    cell: {
                      borderBottom: '1px solid var(--color-divider)',
                      cursor: 'pointer',
                    },
                  }}
                  muiTableContainerProps={{
                    sx: { overflowY: 'visible', overflowX: 'auto' },
                  }}
                  dropdownMenu={
                    ((service: ServiceRecord) => (
                      <div className="w-100 d-flex flex-column gap-2 text-theme">
                        <div className="d-flex gap-3">
                          <span>CPU: {service.cpu}%</span>
                          <span>Memory: {service.memory}</span>
                        </div>
                      </div>
                    )) as () => JSX.Element
                  }
                />
              </div>

              {/* Mobile accordion */}
              <div className="d-block d-lg-none">
                {filteredServices.map((service, index) => (
                  <div
                    className={index !== filteredServices.length - 1 ? 'mb-3' : ''}
                    key={service.id}
                  >
                    <BaseAccordion
                      key={service.id}
                      bodyBackground="var(--color-base)"
                      header={service.name}
                    >
                      <div className="w-100 d-flex flex-column gap-2 text-theme">
                        <div className="d-flex flex-column gap-1 mb-2">
                          <div className="text-theme-muted small">ID: {service.id}</div>
                          <StatusDot view={STATUS_VIEW[service.status] ?? STATUS_VIEW.Unknown} />
                        </div>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="mb-0">
                              <span className="fw-bold">Uptime:</span> {service.uptime}
                            </p>
                            <p className="mb-0">
                              <span className="fw-bold">Service Created:</span> {service.created}
                            </p>
                            <p className="mb-0">
                              <span className="fw-bold">Manual Restarts:</span>{' '}
                              {service.manualRestarts}
                            </p>
                            <p className="mb-0">
                              <span className="fw-bold">Unstable Restarts:</span>{' '}
                              {service.unstableRestart}
                            </p>
                          </div>

                          {canBeModified(service) ? (
                            <BaseEllipsisMenu
                              options={[
                                {
                                  component: (
                                    <div className="w-100 d-flex flex-column">
                                      <Button
                                        className="w-100 px-5 text-center cursor-pointer text-theme border border-1 border-black"
                                        variant="danger"
                                        onClick={() => handleRemoveService(service)}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  ),
                                },
                              ]}
                              open={mobileRowIdEllipsisOpen[service.id]}
                              onOpen={() =>
                                setMobileRowIdEllipsisOpen({
                                  ...mobileRowIdEllipsisOpen,
                                  [service.id]: true,
                                })
                              }
                              onClose={() =>
                                setMobileRowIdEllipsisOpen({
                                  ...mobileRowIdEllipsisOpen,
                                  [service.id]: false,
                                })
                              }
                            />
                          ) : (
                            <div className="text-theme-muted small"></div>
                          )}
                        </div>

                        <p className="mb-0">
                          <span className="fw-bold">CPU:</span> {service.cpu}%
                        </p>
                        <p className="mb-0">
                          <span className="fw-bold">Memory:</span> {service.memory}
                        </p>

                        <div className="d-flex gap-2 mt-2">
                          {!canRestart(service) ? (
                            <div className="text-theme-muted small">{noActionsReason(service)}</div>
                          ) : (
                            <ActionButtons
                              status={service.status}
                              restartOnly={!canBeModified(service)}
                              onChange={(action) => handleActionChange(service, action)}
                            />
                          )}
                        </div>
                      </div>
                    </BaseAccordion>
                  </div>
                ))}
              </div>
            </>
          )}

          {hasGlobals && (
            <>
              <div className="text-theme fw-semibold mb-1 mt-4">Global services</div>
              <div className="text-theme-muted small mb-2">
                One shared instance per supervisor rather than one per deployment.
              </div>
              <div className="d-none d-lg-block">
                <BaseTable
                  columns={columns}
                  rows={filteredGlobalServices}
                  expandable
                  onRowClick={() => {}}
                  customStyles={{
                    cell: {
                      borderBottom: '1px solid var(--color-divider)',
                      cursor: 'pointer',
                    },
                  }}
                  muiTableContainerProps={{
                    sx: { overflowY: 'visible', overflowX: 'auto' },
                  }}
                  dropdownMenu={
                    ((service: ServiceRecord) => (
                      <div className="w-100 d-flex flex-column gap-2 text-theme">
                        <div className="d-flex gap-3">
                          <span>CPU: {service.cpu}%</span>
                          <span>Memory: {service.memory}</span>
                        </div>
                      </div>
                    )) as () => JSX.Element
                  }
                />
              </div>

              {/* Mobile accordion for globals */}
              <div className="d-block d-lg-none">
                {filteredGlobalServices.map((service, index) => (
                  <div
                    className={index !== filteredGlobalServices.length - 1 ? 'mb-3' : ''}
                    key={service.id}
                  >
                    <BaseAccordion
                      key={service.id}
                      bodyBackground="var(--color-base)"
                      header={service.name}
                    >
                      <div className="w-100 d-flex flex-column gap-2 text-theme">
                        <div className="d-flex flex-column gap-1 mb-2">
                          <div className="text-theme-muted small">ID: {service.id}</div>
                          <StatusDot view={STATUS_VIEW[service.status] ?? STATUS_VIEW.Unknown} />
                        </div>
                        <div>
                          <p className="mb-0">
                            <span className="fw-bold">Uptime:</span> {service.uptime}
                          </p>
                          <p className="mb-0">
                            <span className="fw-bold">Manual Restarts:</span>{' '}
                            {service.manualRestarts}
                          </p>
                          <p className="mb-0">
                            <span className="fw-bold">Unstable Restarts:</span>{' '}
                            {service.unstableRestart}
                          </p>
                          <p className="mb-0">
                            <span className="fw-bold">CPU:</span> {service.cpu}%
                          </p>
                          <p className="mb-0">
                            <span className="fw-bold">Memory:</span> {service.memory}
                          </p>
                        </div>
                        <div className="text-theme-muted small mt-2">
                          {noActionsReason(service)}
                        </div>
                      </div>
                    </BaseAccordion>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="d-flex justify-content-end align-items-center gap-2 mt-3 px-0 flex-shrink-0">
            <Button variant="primary" className="px-4" onClick={() => setShowHistoryModal(true)}>
              Service Command History
            </Button>
            <Button variant="primary" className="px-4" onClick={() => setShowAddModal(true)}>
              Add Service
            </Button>
          </div>
        </div>
      )}
      <AddServiceModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddService}
        availableServices={removedServices}
      />
      <CommandHistoryModal
        show={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        services={services}
      />
      <ServiceActionModal
        show={!!pendingAction}
        action={pendingAction?.action}
        serviceName={pendingAction?.service?.name}
        onConfirm={handleActionConfirm}
        onCancel={handleActionCancel}
      />
      <ServiceManagerHelpModal
        show={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        showDeployments={showDeployments}
      />
    </Container>
  );
}
